import React from 'react';

import { Button } from '../base/Button';
import { Modal } from '../../layouts/Modal';
import { TextField } from '../base/TextField';
import { Form } from '../base/Form/Form';
import { IFaultAction } from '../../types/Fault/FaultCause';
import useSuperAdminFaultActionMutations from '../../data/fault/superadmin/useSuperAdminFaultActionMutations';
import { TextArea } from '../base/TextArea';

export type FaultActionModalMode = 'create' | 'edit' | 'clone';

interface CreateEditFaultActionProps {
  open: boolean;
  onClose: (refresh?: boolean) => void;
  faultAction?: IFaultAction;
  mode?: FaultActionModalMode;
  onClone?: (createdAction: IFaultAction) => void;
}

interface FaultActionFormFields {
  title: string;
  description: string;
}

export default function CreateEditFaultAction({
  open,
  onClose,
  faultAction,
  onClone,
  mode = faultAction ? 'edit' : 'create',
}: CreateEditFaultActionProps) {
  const { createFaultAction, updateFaultAction } =
    useSuperAdminFaultActionMutations();

  const handleClose = () => onClose(false);

  const handleCreate = (data: FaultActionFormFields) =>
    createFaultAction.mutate(data, {
      onSuccess: (res) => {
        onClose(true);
        if (
          mode === 'clone' &&
          onClone &&
          res.data &&
          res.data.data &&
          res.data.data.length > 0
        ) {
          onClone(res.data.data[0]);
        }
      },
    });

  const handleUpdate = (data: FaultActionFormFields) =>
    updateFaultAction.mutate(
      {
        ...faultAction!,
        ...data,
      },
      {
        onSuccess: () => onClose(true),
      }
    );

  const getTitle = () => {
    switch (mode) {
      case 'create':
        return 'Create Fault Action';
      case 'edit':
        return 'Edit Fault Action';
      case 'clone':
        return 'Clone Fault Action';
      default:
        return 'Fault Action';
    }
  };

  const getDescription = () => {
    switch (mode) {
      case 'create':
        return 'Use this form to create a new fault action';
      case 'edit':
        return 'Use this form to edit the fault action';
      case 'clone':
        return 'Use this form to create a copy of the selected fault action';
      default:
        return 'Use this form to manage the fault action';
    }
  };

  const getDefaultValues = () => {
    if (!faultAction) return {};

    if (mode === 'clone') {
      return {
        title: `${faultAction.title} (Copy)`,
        description: faultAction.description,
      };
    }

    return {
      title: faultAction.title,
      description: faultAction.description,
    };
  };

  const getSubmitButtonText = () => {
    switch (mode) {
      case 'create':
        return 'Create';
      case 'edit':
        return 'Update';
      case 'clone':
        return 'Clone';
      default:
        return 'Save';
    }
  };

  const handleSubmit = (data: FaultActionFormFields) => {
    if (mode === 'create' || mode === 'clone') {
      handleCreate(data);
    } else {
      handleUpdate(data);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={getTitle()}
      description={getDescription()}
    >
      <Form<FaultActionFormFields>
        defaultValues={getDefaultValues()}
        onSubmit={handleSubmit}
        className="mt-4 grid grid-cols-1 gap-y-4"
      >
        {({ register }) => (
          <>
            <TextField
              {...register('title', { required: true })}
              label="Title"
            />
            <TextArea
              {...register('description', { required: true })}
              label="Description"
            />
            <div className="mt-6 space-x-4">
              <Button type="submit">{getSubmitButtonText()}</Button>
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
            </div>
          </>
        )}
      </Form>
    </Modal>
  );
}
